import Phaser from 'phaser';
import { APP_CONFIG, GAMEPLAY_CONFIG } from './config';
import { AppState } from '../domain/state/AppState';
import type { PowerUpType } from '../domain/powerups/PowerUpSpawnManager';
import { GameStateMachine } from '../domain/state/GameStateMachine';
import {
  ExperienceScene,
  type IntroSceneVisualDebug,
  type ShipVisualState,
} from '../game/scenes/ExperienceScene';
import type { GameWorldSnapshot } from '../game/systems/GameWorld';
import type { SpaceBackgroundDebug } from '../game/background/SpaceBackgroundController';
import { KeyboardInputAdapter } from '../input/KeyboardInputAdapter';
import { IntroTimeline, type ScrollDebugSnapshot } from '../intro/IntroTimeline';
import { AudioSystem } from '../services/AudioSystem';
import { EventBus } from '../services/EventBus';
import { Logger } from '../services/Logger';
import { SeededRandom } from '../services/RandomSource';
import { StorageService } from '../services/StorageService';
import { UiController } from '../ui/UiController';
import { LeaderboardController } from '../features/leaderboard/LeaderboardController';
import { GlobalBestController } from '../features/leaderboard/GlobalBestController';
import { SupabaseLeaderboardRepository } from '../features/leaderboard/services/leaderboard-service';
import { LeaderboardStorage } from '../features/leaderboard/services/leaderboard-storage';
import { supabase } from '../lib/supabase/client';

declare global {
  interface Window {
    __GAME_DEBUG__?: {
      getState: () => AppState;
      getSnapshot: () => ReturnType<App['getSnapshot']>;
      damagePlayer: () => void;
      forceGameOver: () => void;
      getIntroProgress: () => number;
      getScrollTriggerCount: () => number;
      getScrollControllerCount: () => number;
      getScrollDebug: () => ScrollDebugSnapshot | undefined;
      getInputDebug: () => { phaserPreventDefaultWheel: boolean | undefined };
      setAmmo: (ammo: number) => void;
      spawnPowerUp: (type: PowerUpType) => void;
      collectPowerUp: (type: PowerUpType) => void;
      toggleHitboxes: () => boolean;
      getShipPosition: () => { x: number; y: number };
      getShipVisualState: () => ShipVisualState;
      getBackgroundDebug: () => SpaceBackgroundDebug | undefined;
      getIntroVisualDebug: () => IntroSceneVisualDebug | undefined;
    };
  }
}

export class App {
  private readonly logger = new Logger(APP_CONFIG.debug);
  private readonly events = new EventBus();
  private readonly machine = new GameStateMachine(AppState.Booting, this.logger);
  private readonly storage = new StorageService(window.localStorage, this.logger);
  private readonly persisted = this.storage.load();
  private readonly audio = new AudioSystem(this.persisted.muted, this.logger);
  private readonly touch = { axis: { x: 0, y: 0 }, fire: false };
  private readonly input = new KeyboardInputAdapter(
    () =>
      !this.isLeaderboardDialogOpen() &&
      [AppState.Ready, AppState.Playing, AppState.Hit, AppState.Paused, AppState.GameOver].includes(
        this.machine.state,
      ),
  );
  private readonly ui: UiController;
  private readonly leaderboard: LeaderboardController;
  private readonly globalBest: GlobalBestController;
  private readonly leaderboardRepository = new SupabaseLeaderboardRepository(supabase);
  private game?: Phaser.Game;
  private scene?: ExperienceScene;
  private intro?: IntroTimeline;
  private replayInProgress = false;
  private cleanup: Array<() => void> = [];

  public constructor(private readonly root: HTMLElement) {
    this.ui = new UiController(this.root, this.machine, this.events, {
      confirm: () => this.confirm('other'),
      togglePause: () => this.togglePause(),
      toggleMute: () => this.toggleMute(),
      replayIntro: () => this.replayIntro(),
      openLeaderboard: (opener) => this.openLeaderboard(opener),
    });
    this.leaderboard = new LeaderboardController(
      this.root,
      this.events,
      this.leaderboardRepository,
      new LeaderboardStorage(window.localStorage),
    );
    this.globalBest = new GlobalBestController(this.events, this.leaderboardRepository);
  }

  public start(): void {
    this.globalBest.start();
    this.cleanup.push(this.input.attach());
    this.cleanup.push(
      this.machine.subscribe((from, to) => {
        this.events.emit('app:state-changed', { from, to });
        if (import.meta.env.DEV && to === AppState.Ready) {
          requestAnimationFrame(() => this.validateReadyVisualState());
        }
      }),
    );
    this.attachGlobalControls();
    this.attachTouchControls();
    this.machine.transition(AppState.Loading);
    const scene = new ExperienceScene({
      machine: this.machine,
      events: this.events,
      input: this.input,
      storage: this.storage,
      audio: this.audio,
      random: new SeededRandom(Date.now()),
      highScore: this.persisted.highScore,
      touch: this.touch,
      onReady: (readyScene) => this.onSceneReady(readyScene),
    });
    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.CANVAS,
      parent: 'game-canvas-container',
      backgroundColor: '#050714',
      width: APP_CONFIG.baseWidth,
      height: APP_CONFIG.baseHeight,
      pixelArt: true,
      antialias: false,
      roundPixels: true,
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        width: APP_CONFIG.baseWidth,
        height: APP_CONFIG.baseHeight,
      },
      scene: [scene],
      input: {
        mouse: {
          preventDefaultWheel: false,
        },
      },
      audio: { disableWebAudio: true },
      render: { antialias: false, pixelArt: true, roundPixels: true },
    };
    this.game = new Phaser.Game(config);
  }

  public destroy(): void {
    this.intro?.destroy();
    this.setGameplayScrollLock(false);
    this.cleanup.forEach((dispose) => dispose());
    this.cleanup = [];
    this.audio.destroy();
    this.globalBest.destroy();
    this.leaderboard.destroy();
    this.ui.destroy();
    this.events.clear();
    this.game?.destroy(true);
    delete window.__GAME_DEBUG__;
  }
  private onSceneReady(scene: ExperienceScene): void {
    this.scene = scene;
    this.ui.finishLoading();
    this.ui.setMobileCopy(matchMedia('(hover: none), (pointer: coarse)').matches);
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const debugReady =
      import.meta.env.DEV && new URLSearchParams(window.location.search).has('ready');
    if (reducedMotion || this.persisted.skipIntro || debugReady) {
      scene.renderIntroProgress(1);
      this.machine.transition(AppState.Ready);
    } else {
      this.machine.transition(AppState.Intro);
      this.createIntroController();
    }
    this.events.emit('audio:mute-changed', { muted: this.audio.isMuted });
    if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
      window.__GAME_DEBUG__ = {
        getState: () => this.machine.state,
        getSnapshot: () => this.getSnapshot(),
        damagePlayer: () => this.scene?.damagePlayerForTest(),
        forceGameOver: () => this.scene?.gameOverForTest(),
        getIntroProgress: () => this.scene?.currentIntroProgress ?? 0,
        getScrollTriggerCount: () => this.intro?.activeTriggerCount ?? 0,
        getScrollControllerCount: () => this.intro?.activeControllerCount ?? 0,
        getScrollDebug: () => this.intro?.debugSnapshot,
        getInputDebug: () => ({
          phaserPreventDefaultWheel: this.game?.config.inputMousePreventDefaultWheel,
        }),
        setAmmo: (ammo) => this.scene?.setAmmoForTest(ammo),
        spawnPowerUp: (type) => this.scene?.spawnPowerUpForTest(type),
        collectPowerUp: (type) => this.scene?.collectPowerUpForTest(type),
        toggleHitboxes: () => this.scene?.toggleHitboxesForDebug() ?? false,
        getShipPosition: () => this.scene?.currentShipPosition ?? { x: 0, y: 0 },
        getShipVisualState: () =>
          this.scene?.currentShipVisualState ?? {
            active: false,
            visible: false,
            alpha: 0,
            scaleX: 0,
            scaleY: 0,
            rotation: 0,
          },
        getBackgroundDebug: () => this.scene?.backgroundDebug,
        getIntroVisualDebug: () => this.scene?.introVisualDebug,
      };
    }
  }

  private confirm(source: 'space' | 'other'): void {
    void this.audio.unlock().then(() => this.audio.startMusic());
    if (this.machine.state === AppState.Ready) {
      if (source === 'space') this.input.consumeStartSpace();
      else this.input.clearFireSuppression();
      this.audio.play('confirm');
      this.machine.transition(AppState.Playing);
      this.intro?.destroy();
      this.intro = undefined;
      this.setGameplayScrollLock(true);
      this.scene?.startGame();
      this.root.querySelector<HTMLElement>('#game-shell')?.focus();
    } else if (this.machine.state === AppState.GameOver) {
      if (source === 'space') this.input.consumeStartSpace();
      else this.input.clearFireSuppression();
      this.machine.transition(AppState.Restarting);
      this.scene?.resetGame();
      this.machine.transition(AppState.Ready);
      this.machine.transition(AppState.Playing);
      this.scene?.startGame();
    }
  }

  private togglePause(): void {
    if (this.machine.state === AppState.Playing || this.machine.state === AppState.Hit) {
      if (this.machine.state === AppState.Hit) return;
      this.machine.transition(AppState.Paused);
      this.audio.setPaused(true);
    } else if (this.machine.state === AppState.Paused) {
      this.machine.transition(AppState.Playing);
      this.audio.setPaused(false);
      this.root.querySelector<HTMLElement>('#game-shell')?.focus();
    }
  }

  private toggleMute(): void {
    const muted = !this.audio.isMuted;
    this.audio.setMuted(muted);
    this.storage.save({ muted });
    this.events.emit('audio:mute-changed', { muted });
  }

  private replayIntro(): void {
    void this.resetDemo();
  }

  private openLeaderboard(opener: HTMLButtonElement): void {
    if (this.machine.state !== AppState.Ready) return;
    this.input.reset();
    this.leaderboard.openIntro(opener);
  }

  private isLeaderboardDialogOpen(): boolean {
    return this.leaderboard.isDialogOpen;
  }

  private async resetDemo(): Promise<void> {
    if (!this.scene || this.replayInProgress) return;
    if (this.machine.state !== AppState.Ready && this.machine.state !== AppState.GameOver) return;
    this.replayInProgress = true;
    try {
      this.intro?.destroy();
      this.intro = undefined;
      this.audio.stopMusic();
      this.input.reset();
      this.touch.axis.x = 0;
      this.touch.axis.y = 0;
      this.touch.fire = false;
      this.setGameplayScrollLock(false);
      this.scene.resetDemo();
      this.machine.transition(AppState.Intro);
      const sectionTop = document.querySelector<HTMLElement>('#experience')?.offsetTop ?? 0;
      window.scrollTo({ top: sectionTop, behavior: 'auto' });
      await this.nextLayoutFrame();
      await this.nextLayoutFrame();
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.scene.renderIntroProgress(1);
        this.machine.transition(AppState.Ready);
      } else {
        this.createIntroController();
        this.scene.resetDemo();
      }
    } finally {
      this.replayInProgress = false;
    }
  }

  private createIntroController(): void {
    if (!this.scene) return;
    this.intro?.destroy();
    const debugEnabled =
      APP_CONFIG.debug && new URLSearchParams(window.location.search).has('scrollDebug');
    this.intro = new IntroTimeline(this.scene, this.machine, debugEnabled);
    this.intro.create();
  }

  private nextLayoutFrame(): Promise<void> {
    return new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  private setGameplayScrollLock(active: boolean): void {
    document.documentElement.classList.toggle('game-active', active);
    document.body.classList.toggle('game-active', active);
  }

  private validateReadyVisualState(): void {
    if (this.machine.state !== AppState.Ready || !this.scene) return;
    const visual = this.scene.introVisualDebug;
    const ready = this.root.querySelector<HTMLElement>('#ready');
    const readyVisible = ready ? getComputedStyle(ready).visibility === 'visible' : false;
    if (!visual.ship.visible || !visual.ship.active || visual.ship.alpha <= 0) {
      console.error('Invalid READY state: intro ship is not visible.');
    }
    if (visual.spaceBackground.visibleChunkCount === 0 || !visual.sky.visible) {
      console.error('Invalid READY state: space background is not visible.');
    }
    if (!readyVisible) console.error('Invalid READY state: ready prompt is not visible.');
  }

  private attachGlobalControls(): void {
    const keydown = (event: KeyboardEvent): void => {
      if (this.leaderboard.isDialogOpen) return;
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement ||
        (event.target instanceof HTMLElement && event.target.isContentEditable)
      ) {
        return;
      }
      if (event.repeat && ['Space', 'Enter', 'KeyP', 'Escape', 'KeyM'].includes(event.code)) return;
      if (
        (event.code === 'Space' || event.code === 'Enter') &&
        [AppState.Ready, AppState.GameOver].includes(this.machine.state)
      ) {
        event.preventDefault();
        this.confirm(event.code === 'Space' ? 'space' : 'other');
      } else if (
        (event.code === 'KeyP' || event.code === 'Escape') &&
        [AppState.Playing, AppState.Paused].includes(this.machine.state)
      ) {
        event.preventDefault();
        this.togglePause();
      } else if (event.code === 'KeyM') {
        this.toggleMute();
      }
    };
    const visibility = (): void => {
      if (document.hidden && this.machine.state === AppState.Playing) this.togglePause();
    };
    window.addEventListener('keydown', keydown, { passive: false });
    document.addEventListener('visibilitychange', visibility);
    this.cleanup.push(() => window.removeEventListener('keydown', keydown));
    this.cleanup.push(() => document.removeEventListener('visibilitychange', visibility));
  }

  private attachTouchControls(): void {
    const pad = this.root.querySelector<HTMLElement>('#touch-pad');
    const knob = pad?.querySelector<HTMLElement>('span');
    const fire = this.root.querySelector<HTMLButtonElement>('#fire-button');
    if (!pad || !knob || !fire) return;
    const move = (event: PointerEvent): void => {
      const bounds = pad.getBoundingClientRect();
      const x = Phaser.Math.Clamp(((event.clientX - bounds.left) / bounds.width) * 2 - 1, -1, 1);
      const y = Phaser.Math.Clamp(((event.clientY - bounds.top) / bounds.height) * 2 - 1, -1, 1);
      this.touch.axis.x = x;
      this.touch.axis.y = y;
      knob.style.transform = `translate(${Math.round(x * 30)}px, ${Math.round(y * 30)}px)`;
    };
    const resetPad = (): void => {
      this.touch.axis.x = 0;
      this.touch.axis.y = 0;
      knob.style.transform = '';
    };
    const fireDown = (event: PointerEvent): void => {
      event.preventDefault();
      this.touch.fire = true;
    };
    const fireUp = (): void => {
      this.touch.fire = false;
    };
    pad.addEventListener('pointerdown', move);
    pad.addEventListener('pointermove', (event) => {
      if (event.buttons > 0) move(event);
    });
    pad.addEventListener('pointerup', resetPad);
    pad.addEventListener('pointercancel', resetPad);
    fire.addEventListener('pointerdown', fireDown);
    fire.addEventListener('pointerup', fireUp);
    fire.addEventListener('pointercancel', fireUp);
  }

  private getSnapshot(): {
    state: AppState;
    score: number;
    lives: number;
    activeAsteroids: number;
    activeBullets: number;
    ammo: number;
    maxAmmo: number;
    activePowerUps: number;
    asteroidVariantIds: string[];
    powerUpDebug: GameWorldSnapshot['powerUpDebug'];
  } {
    return {
      state: this.machine.state,
      score: this.scene?.debugSnapshot.score ?? 0,
      lives: this.scene?.debugSnapshot.lives ?? 3,
      activeAsteroids: this.scene?.debugSnapshot.activeAsteroids ?? 0,
      activeBullets: this.scene?.debugSnapshot.activeBullets ?? 0,
      ammo: this.scene?.debugSnapshot.ammo ?? GAMEPLAY_CONFIG.weapon.initialAmmo,
      maxAmmo: this.scene?.debugSnapshot.maxAmmo ?? GAMEPLAY_CONFIG.weapon.maxAmmo,
      activePowerUps: this.scene?.debugSnapshot.activePowerUps ?? 0,
      asteroidVariantIds: this.scene?.debugSnapshot.asteroidVariantIds ?? [],
      powerUpDebug: this.scene?.debugSnapshot.powerUpDebug ?? {
        elapsedMs: 0,
        nextSpawnAtMs: 0,
        timeSinceAmmoMs: 0,
        timeSinceLifeMs: 0,
        ammoWeight: GAMEPLAY_CONFIG.powerUps.ammo.baseWeight,
        lifeWeight: GAMEPLAY_CONFIG.powerUps.life.baseWeight,
      },
    };
  }
}
